import { ftAccessToken, ftRefreshToken } from "../constants/Constants";
import { refreshToken } from "./AuthService";
import { properties } from "../properties";
import { responseError, isRateLimited } from "./HttpErrors";

/**
 * Generic JSON request. Handles auth, token refresh, and JSON serialization.
 * @param {string} method - HTTP method (GET, POST, PUT, DELETE, etc.)
 * @param {string} endpoint - Path after the host (e.g. "/manuscript/all")
 * @param {object|null} body - Request body (will be JSON stringified)
 * @param {boolean} firstCall - Internal flag for token refresh retry
 * @returns {Promise<any>}
 */
export async function request(method, endpoint, body = null, firstCall = true) {
    const accessToken = localStorage.getItem(ftAccessToken)?.replaceAll('"', '');

    const headers = { "Content-Type": "application/json;charset=UTF-8" };
    if (accessToken) headers["Authorization"] = "Bearer " + accessToken;

    const response = await fetch(properties.host + endpoint, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
    });

    if (response.ok) {
        return response.json();
    }

    if ((response.status === 401 || response.status === 403) && firstCall) {
        try {
            const newToken = await refreshToken();
            localStorage.setItem(ftAccessToken, JSON.stringify(newToken));
            return request(method, endpoint, body, false);
        } catch (err) {
            // Rate limited, not logged out: keep the tokens so a later call can refresh
            if (isRateLimited(err)) throw err;
            localStorage.removeItem(ftAccessToken);
            localStorage.removeItem(ftRefreshToken);
            throw { error: new Error("Failed to refresh token."), status: response.status };
        }
    }

    throw await responseError(response);
}

/**
 * Fire-and-forget page-view beacon for the admin User Activity page.
 *
 * Deliberately NOT request(): the backend JWT filter 403s on an *expired*
 * Bearer token even though /activity/** is permit-all, and request()'s
 * refresh path hard-redirects to "/" on a dead session — unacceptable for a
 * background beacon on public pages (see fetchPublicFeatureFlags for the
 * same pattern). A valid token is still sent so the view is attributed;
 * with a stale token the view is silently dropped, never refreshed/retried.
 */
export function trackPageView(path) {
    const accessToken = localStorage.getItem(ftAccessToken)?.replaceAll('"', '');
    const headers = { "Content-Type": "application/json;charset=UTF-8" };
    if (accessToken) headers["Authorization"] = "Bearer " + accessToken;
    fetch(properties.host + "/activity/page", {
        method: "POST",
        headers,
        body: JSON.stringify({ path }),
        keepalive: true,
    }).catch(() => {});
}
